import { apiBase } from "@/lib/api";
import type { MailAttachment } from "@/lib/api";
import {
  blobToBase64,
  canNativePreviewFile,
  canNativeSaveFile,
  nativePreviewFile,
  nativeSaveFile,
} from "../lib/hubShell";

function hubApiBase(): string {
  try {
    return (apiBase() || "").replace(/\/$/, "");
  } catch {
    return "";
  }
}

/** Prefer browser-ready fields already on the attachment object. */
export function attachmentOpenUrl(file: MailAttachment): string {
  // Prefer dataUrl — it loads in <img>/<iframe> without Bearer.
  return file.dataUrl || file.url || file.downloadUrl || "";
}

/**
 * Pick a href the browser can load without our session cookie/Bearer.
 * Prefers data:/blob:/CDN over api.hub /api paths that would 401 in a new tab.
 */
export function pickOpenableAttachmentHref(parts: {
  dataUrl?: string;
  url?: string;
  downloadUrl?: string;
}): string {
  for (const cand of [parts.dataUrl, parts.url, parts.downloadUrl]) {
    if (!cand) continue;
    const abs = absolutizeAttachmentHref(cand);
    if (isDirectlyOpenableHref(cand) || isDirectlyOpenableHref(abs)) {
      return abs;
    }
  }
  return "";
}

export function isImageAttachment(file: MailAttachment): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  const name = (file.filename || "").toLowerCase();
  return mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(name);
}

export function isPdfAttachment(file: MailAttachment): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  const name = (file.filename || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

/** Make relative /api paths absolute against the Lightsail API host. */
export function absolutizeAttachmentHref(href: string): string {
  if (!href) return "";
  if (
    href.startsWith("data:") ||
    href.startsWith("blob:") ||
    /^https?:\/\//i.test(href)
  ) {
    return href;
  }
  if (href.startsWith("/")) {
    const base = hubApiBase();
    return base ? `${base}${href}` : href;
  }
  return href;
}

/**
 * True when a new tab can load the href without our Bearer token.
 * API-host URLs and bare /api paths need an authenticated fetch instead.
 */
export function isDirectlyOpenableHref(href: string): boolean {
  if (!href) return false;
  if (href.startsWith("data:") || href.startsWith("blob:")) return true;
  if (href.startsWith("/")) return false;
  if (/^https?:\/\//i.test(href)) {
    const base = hubApiBase();
    if (base && href.startsWith(base)) return false;
    return true;
  }
  return false;
}

export function canPreviewAttachment(file: MailAttachment, href: string): boolean {
  if (!href) return false;
  if (isImageAttachment(file) || href.startsWith("data:image/")) return true;
  if (isPdfAttachment(file) || href.startsWith("data:application/pdf")) return true;
  return false;
}

export function triggerDownload(href: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename || "attachment";
  anchor.rel = "noopener";
  // Tip 140 skip + tip 142: keep download from being forced into a new-tab open.
  anchor.setAttribute("data-hub-same-tab", "1");
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function isIosLike(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS desktop UA
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/** Load href bytes (blob:/data:/https:) for download or share. */
export async function hrefToBlob(href: string): Promise<Blob> {
  const res = await fetch(href);
  if (!res.ok) throw new Error(`Could not load file (${res.status}).`);
  const blob = await res.blob();
  if (!blob.size) throw new Error("File was empty.");
  return blob;
}

function asFile(blob: Blob, filename: string): File {
  const name = filename || "attachment";
  const type = blob.type || "application/octet-stream";
  try {
    return new File([blob], name, { type });
  } catch {
    // Older WebKit: File constructor may throw — Blob still works for object URLs.
    return Object.assign(blob, { name, lastModified: Date.now() }) as File;
  }
}

/**
 * Tip 142: reliable download on iPhone (blob:/data: + download attr is a no-op).
 * Uses the share sheet on iOS when possible so the user can Save Image / Files.
 */
export async function downloadAttachmentHref(href: string, filename: string): Promise<void> {
  const blob = await hrefToBlob(href);
  const file = asFile(blob, filename);
  // Tip 213: Mac Hub WKWebView does not honor <a download>; save via NSSavePanel.
  // iOS uses the same bridge for the share sheet (Save to Files / Open in).
  if (canNativeSaveFile()) {
    const base64 = await blobToBase64(blob);
    if (nativeSaveFile({ filename, mimeType: blob.type || file.type, base64 })) return;
  }
  if (
    isIosLike() &&
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({ files: [file], title: filename });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      throw new Error("Could not open the share sheet for this file.");
    }
  }
  // An <a download> click in the iOS app replaces Hub with the file and there is no way back.
  if (isIosLike()) {
    throw new Error("Use Share or Open with to save this file.");
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    triggerDownload(objectUrl, filename);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }
}

/**
 * Tip 142: system share sheet when available; otherwise download.
 * Returns "shared" | "downloaded" | "cancelled".
 */
export async function shareAttachmentHref(
  href: string,
  filename: string,
): Promise<"shared" | "downloaded" | "cancelled"> {
  const blob = await hrefToBlob(href);
  const file = asFile(blob, filename);
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return "shared";
      }
      if (/^https?:\/\//i.test(href)) {
        await navigator.share({ url: href, title: filename });
        return "shared";
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return "cancelled";
      /* fall through */
    }
  }
  await downloadAttachmentHref(href, filename);
  return "downloaded";
}

/**
 * iOS Hub: Quick Look when the app bridge exists, otherwise the system share sheet.
 * Returns "preview" when Quick Look was presented.
 */
export async function openWithAttachmentHref(
  href: string,
  filename: string,
  mimeType?: string,
): Promise<"preview" | "shared" | "downloaded" | "cancelled"> {
  if (canNativePreviewFile()) {
    const blob = await hrefToBlob(href);
    const base64 = await blobToBase64(blob);
    if (
      nativePreviewFile({
        filename,
        mimeType: mimeType || blob.type || "application/octet-stream",
        base64,
      })
    ) {
      return "preview";
    }
  }
  return shareAttachmentHref(href, filename);
}
